import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaMedium,
  FaExternalLinkAlt,
  FaArrowRight,
  FaTag,
  FaClock,
  FaEye,
  FaBookmark,
  FaShare,
  FaStar,
} from "react-icons/fa";
import { getPublishedLocalPosts } from "../../data/blogPosts";
import appleImage from "../../assets/images/apple.jpg";

const FloatingParticle = ({ delay, duration, x, y }) => (
  <motion.div
    className="absolute w-2 h-2 bg-blue-400/20 rounded-full"
    initial={{ opacity: 0, x, y }}
    animate={{
      opacity: [0, 1, 0],
      x: x + Math.random() * 100 - 50,
      y: y - 100,
    }}
    transition={{
      duration,
      repeat: Infinity,
      delay,
      ease: "easeInOut",
    }}
  />
);

const SkeletonLoader = () => (
  <div className="animate-pulse bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-2xl">
    <div className="h-56 bg-gradient-to-br from-white/10 to-white/5 rounded-xl mb-6"></div>
    <div className="h-6 bg-white/20 rounded-lg mb-4"></div>
    <div className="h-4 bg-white/15 rounded-lg mb-3 w-3/4"></div>
    <div className="h-4 bg-white/15 rounded-lg w-1/2"></div>
    <div className="flex justify-between items-center mt-6">
      <div className="h-4 bg-white/10 rounded w-20"></div>
      <div className="h-8 bg-white/10 rounded-lg w-24"></div>
    </div>
  </div>
);

const BlogCard = ({ blog, index, onClick }) => (
  <motion.div
    className="group cursor-pointer"
    initial={{ opacity: 0, y: 40, rotateY: -10 }}
    animate={{ opacity: 1, y: 0, rotateY: 0 }}
    transition={{
      duration: 0.8,
      delay: index * 0.15,
      type: "spring",
      stiffness: 100,
    }}
    whileHover={{ y: -12, rotateY: 5 }}
    onClick={onClick}
  >
    <div className="relative bg-gradient-to-br from-gray-900/80 via-gray-800/90 to-gray-900/80 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl hover:shadow-blue-500/25 transition-all duration-500 h-full flex flex-col hover:border-blue-400/50">
      {/* Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Image Section */}
      <div className="relative overflow-hidden h-56">
        <img
          src={blog.thumbnail || appleImage}
          alt={blog.title}
          className="w-full h-full object-contain bg-gray-800 transition-all duration-700 group-hover:scale-110 group-hover:rotate-1"
          onError={(e) => {
            // Prevent infinite loop by checking if already using fallback
            if (!e.target.src.includes("apple.jpg")) {
              e.target.src = appleImage;
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>

        {/* Type Badge */}
        <div className="absolute top-4 right-4">
          <motion.div
            className={`p-3 rounded-xl backdrop-blur-xl shadow-lg ${
              blog.type === "medium"
                ? "bg-green-500/80 border border-green-400/50"
                : "bg-blue-500/80 border border-blue-400/50"
            }`}
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            {blog.type === "medium" ? (
              <FaMedium className="text-white text-lg" />
            ) : (
              <FaStar className="text-white text-lg" />
            )}
          </motion.div>
        </div>

        {/* Featured Tag */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="absolute bottom-4 left-4">
            <motion.div
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600/90 to-purple-600/90 backdrop-blur-xl px-3 py-2 rounded-full border border-white/20"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 + 0.5 }}
            >
              <FaTag className="text-blue-200 text-xs" />
              <span className="text-white text-sm font-medium">
                {blog.tags[0]}
              </span>
            </motion.div>
          </div>
        )}

        {/* Read Time */}
        <div className="absolute top-4 left-4">
          <motion.div
            className="flex items-center gap-2 bg-black/60 backdrop-blur-xl px-3 py-2 rounded-full border border-white/10"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 + 0.3 }}
          >
            <FaClock className="text-blue-400 text-xs" />
            <span className="text-white text-sm">
              {blog.readTime || "5 min"}
            </span>
          </motion.div>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-8 flex-grow flex flex-col relative">
        {/* Title */}
        <h3 className="text-2xl font-bold text-white mb-4 line-clamp-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 group-hover:bg-clip-text transition-all duration-300 leading-tight">
          {blog.title}
        </h3>

        {/* Excerpt */}
        <p className="text-gray-100 mb-6 line-clamp-3 flex-grow leading-relaxed text-lg">
          {blog.excerpt}
        </p>

        {/* Meta Information */}
        <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 text-sm text-gray-200">
            <div className="flex items-center gap-2">
              <FaCalendarAlt className="text-blue-400" />
              <span>{blog.pubDate}</span>
            </div>
          </div>

          <motion.div
            className="flex items-center gap-2 text-blue-400 group-hover:text-blue-300 font-medium"
            whileHover={{ x: 5 }}
          >
            <span className="text-sm">
              {blog.type === "medium" ? "Read on Medium" : "Read Article"}
            </span>
            {blog.type === "medium" ? (
              <FaExternalLinkAlt className="text-sm" />
            ) : (
              <FaArrowRight className="text-sm" />
            )}
          </motion.div>
        </div>

        {/* Hover Actions */}
        <motion.div
          className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300"
          initial={{ y: -10 }}
          whileHover={{ y: 0 }}
        >
          <motion.button
            className="p-2 bg-white/10 backdrop-blur-xl rounded-lg border border-white/20 text-white hover:bg-white/20 transition-all"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.stopPropagation();
              // Handle bookmark functionality
            }}
          >
            <FaBookmark className="text-sm" />
          </motion.button>
          <motion.button
            className="p-2 bg-white/10 backdrop-blur-xl rounded-lg border border-white/20 text-white hover:bg-white/20 transition-all"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.stopPropagation();
              // Handle share functionality
            }}
          >
            <FaShare className="text-sm" />
          </motion.button>
        </motion.div>
      </div>
    </div>
  </motion.div>
);

const BlogList = () => {
  const navigate = useNavigate();
  const [mediumBlogs, setMediumBlogs] = useState([]);
  const [localBlogs, setLocalBlogs] = useState([]);
  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState("all");
  const blogsPerPage = 6;

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        // Load local posts
        const localPosts = getPublishedLocalPosts().map((post) => ({
          ...post,
          type: "local",
          pubDate: new Date(post.date).toDateString(),
          thumbnail: post.image || appleImage,
        }));

        // Fetch Medium posts
        const response = await fetch(
          "https://api.rss2json.com/v1/api.json?rss_url=https://medium.com/feed/@mihirshah99",
        );
        const data = await response.json();
        const mediumPosts = data.items.map((item) => {
          const imageMatch = item.content.match(/<img.*?src="(.*?)"/);
          const imageUrl = imageMatch
            ? imageMatch[1]
            : appleImage;

          return {
            title: item.title,
            link: item.link,
            pubDate: new Date(item.pubDate).toDateString(),
            date: item.pubDate,
            thumbnail: imageUrl,
            excerpt:
              item.description.replace(/(<([^>]+)>)/gi, "").slice(0, 150) +
              "...",
            type: "medium",
          };
        });

        // Combine and sort all posts by date
        const combined = [...localPosts, ...mediumPosts].sort(
          (a, b) => new Date(b.date) - new Date(a.date),
        );

        setLocalBlogs(localPosts);
        setMediumBlogs(mediumPosts);
        setAllBlogs(combined);
        setLoading(false);
      } catch (error) {
        console.error("Error loading blog posts:", error);
        // Still load local posts even if Medium fails
        const localPosts = getPublishedLocalPosts().map((post) => ({
          ...post,
          type: "local",
          pubDate: new Date(post.date).toDateString(),
          thumbnail: appleImage,
        }));
        setLocalBlogs(localPosts);
        setAllBlogs(localPosts);
        setLoading(false);
      }
    };

    loadBlogs();
  }, []);

  // Get filtered blogs based on active tab
  const getFilteredBlogs = () => {
    switch (activeTab) {
      case "local":
        return localBlogs;
      case "medium":
        return mediumBlogs;
      default:
        return allBlogs;
    }
  };

  const filteredBlogs = getFilteredBlogs();
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredBlogs.length / blogsPerPage); i++) {
    pageNumbers.push(i);
  }

  // Reset to page 1 when tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const handleBlogClick = (blog) => {
    if (blog.type === "local") {
      // Navigate to the individual blog post page
      navigate(`/blog/${blog.id}`);
    } else {
      // Open Medium posts in new tab
      window.open(blog.link, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="relative min-h-screen pb-20">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Floating Particles */}
        {[...Array(15)].map((_, i) => (
          <FloatingParticle
            key={i}
            delay={i * 0.5}
            duration={3 + Math.random() * 4}
            x={Math.random() * (typeof window !== "undefined" ? window.innerWidth : 1200)}
            y={(typeof window !== "undefined" ? window.innerHeight : 800) + Math.random() * 100}
          />
        ))}

        {/* Gradient Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        />
      </div>

      {/* Enhanced Hero Section */}
      <div className="relative pb-20 pt-16 text-center overflow-hidden">
        <div className="container mx-auto px-4">
          {/* Main Title */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="relative"
          >
            <h1 className="text-6xl md:text-8xl font-black mb-8 relative">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-gradient-x">
                Security
              </span>
              <br />
              <span
                className="bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent animate-gradient-x"
                style={{ animationDelay: "1s" }}
              >
                Insights
              </span>
            </h1>

            {/* Decorative Elements */}
            <motion.div
              className="absolute -top-4 -right-4 w-24 h-24 border-2 border-blue-400/30 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            />
            <motion.div
              className="absolute -bottom-4 -left-4 w-16 h-16 border-2 border-purple-400/30 rounded-full"
              animate={{ rotate: -360 }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            />
          </motion.div>

          {/* Subtitle */}
          <motion.p
            className="text-2xl md:text-3xl text-gray-100 max-w-4xl mx-auto mb-12 leading-relaxed font-light"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.4 }}
          >
            Diving deep into{" "}
            <span className="text-transparent bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text font-semibold">
              cloud security
            </span>
            ,{" "}
            <span className="text-transparent bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text font-semibold">
              OWASP VXDF
            </span>
            , and the future of cybersecurity
          </motion.p>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="flex flex-wrap justify-center gap-8 mb-12"
          >
            {[
              { icon: FaEye, label: "Readers", count: "10K+" },
              { icon: FaMedium, label: "Medium", count: "Featured" },
              { icon: FaStar, label: "Research", count: "Cited" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                className="flex items-center gap-3 bg-white/5 backdrop-blur-xl px-6 py-4 rounded-2xl border border-white/20"
                whileHover={{ scale: 1.05 }}
              >
                <stat.icon className="text-blue-400 text-xl" />
                <div>
                  <div className="text-white font-bold text-lg">
                    {stat.count}
                  </div>
                  <div className="text-gray-200 text-sm">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 relative">
        {/* Enhanced Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex justify-center mb-16"
        >
          <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-2 shadow-2xl">
            {[
              {
                key: "all",
                label: "All Posts",
                count: allBlogs.length,
                icon: FaStar,
              },
              {
                key: "local",
                label: "Featured",
                count: localBlogs.length,
                icon: FaBookmark,
              },
              {
                key: "medium",
                label: "Medium",
                count: mediumBlogs.length,
                icon: FaMedium,
              },
            ].map((tab) => (
              <motion.button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`relative px-8 py-4 rounded-xl transition-all duration-500 flex items-center gap-3 font-medium ${
                  activeTab === tab.key
                    ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg shadow-blue-500/25"
                    : "text-gray-200 hover:text-white hover:bg-white/10"
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <tab.icon className="text-lg" />
                <span className="text-lg">{tab.label}</span>
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
                  {tab.count}
                </span>
                {activeTab === tab.key && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl -z-10"
                    layoutId="activeTab"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Blog Content */}
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {[...Array(6)].map((_, index) => (
                <SkeletonLoader key={index} />
              ))}
            </motion.div>
          ) : filteredBlogs.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="text-center py-32"
            >
              <div className="bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-16 max-w-md mx-auto">
                <FaBookmark className="text-6xl text-gray-500 mx-auto mb-6" />
                <p className="text-gray-100 text-2xl font-light">
                  No articles found
                </p>
                <p className="text-gray-500 mt-2">
                  Check back soon for new content
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={`blogs-${activeTab}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 mb-16">
                {currentBlogs.map((blog, index) => (
                  <BlogCard
                    key={blog.id || index}
                    blog={blog}
                    index={index}
                    onClick={() => handleBlogClick(blog)}
                  />
                ))}
              </div>

              {/* Enhanced Pagination */}
              {pageNumbers.length > 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-center gap-3"
                >
                  <motion.button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-6 py-3 rounded-xl bg-white/5 backdrop-blur-xl border border-white/20 text-gray-100 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Previous
                  </motion.button>

                  {pageNumbers.map((number) => (
                    <motion.button
                      key={number}
                      onClick={() => setCurrentPage(number)}
                      className={`px-4 py-3 rounded-xl font-medium transition-all ${
                        currentPage === number
                          ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg"
                          : "bg-white/5 backdrop-blur-xl border border-white/20 text-gray-100 hover:bg-white/10"
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {number}
                    </motion.button>
                  ))}

                  <motion.button
                    onClick={() =>
                      setCurrentPage(
                        Math.min(pageNumbers.length, currentPage + 1),
                      )
                    }
                    disabled={currentPage === pageNumbers.length}
                    className="px-6 py-3 rounded-xl bg-white/5 backdrop-blur-xl border border-white/20 text-gray-100 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Next
                  </motion.button>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Enhanced Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="mt-32 text-center"
        >
          <div className="relative bg-gradient-to-br from-blue-900/30 via-purple-900/30 to-pink-900/30 backdrop-blur-xl border border-white/20 rounded-3xl p-16 max-w-4xl mx-auto shadow-2xl overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-20 rounded-3xl" />

            <motion.h3
              className="text-4xl md:text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
            >
              Stay Connected
            </motion.h3>

            <motion.p
              className="text-xl text-gray-100 mb-10 max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4 }}
            >
              Get notified when I publish new research on cloud security, OWASP
              VXDF developments, and cutting-edge cybersecurity insights
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6 }}
            >
              <a
                href="https://mihirshah99.medium.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-2xl transition-all transform hover:scale-105 font-semibold text-lg shadow-lg shadow-blue-500/25"
              >
                <FaMedium className="text-2xl group-hover:rotate-12 transition-transform" />
                Follow on Medium
                <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
              </a>

              <a
                href="https://vxdf.org"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 px-8 py-4 bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/20 text-white rounded-2xl transition-all transform hover:scale-105 font-semibold text-lg"
              >
                <FaStar className="text-xl group-hover:rotate-12 transition-transform" />
                Explore VXDF
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BlogList;
